import { useRef, useState, useEffect } from "react";
import { Eraser, Check } from "lucide-react";

/**
 * Firma sul dispositivo: canvas con input touch/penna/mouse (pointer events).
 * Cancella/rifai prima della conferma. Esporta PNG base64 su conferma.
 * Su desktop senza touch resta utilizzabile col mouse, ma l'alternativa manuale
 * ("Scarica e carica firmato") è sempre offerta dal chiamante.
 */
export function SignaturePad({ onConfirm, signerDefault = "", roles }) {
  const roleList = roles || [{ value: "agente", label: "Agente" }, { value: "titolare_salone", label: "Titolare salone" }];
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const [signer, setSigner] = useState(signerDefault);
  const [role, setRole] = useState(roleList[0].value);
  const [hasInk, setHasInk] = useState(false);
  const multiSigner = Boolean(roles);

  const resetCanvas = () => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, cv.width, cv.height);
    ctx.strokeStyle = "#1f1f1f";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    dirty.current = false;
    setHasInk(false);
  };

  useEffect(() => { resetCanvas(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Al cambio firmatario: azzera canvas e nome (ogni firmatario firma di persona; nessun
  // riutilizzo automatico della firma/nome del firmatario precedente).
  useEffect(() => {
    resetCanvas();
    if (multiSigner) setSigner("");
  }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (canvasRef.current.width / r.width), y: (e.clientY - r.top) * (canvasRef.current.height / r.height) };
  };
  const down = (e) => { drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.target.setPointerCapture?.(e.pointerId); };
  const move = (e) => { if (!drawing.current) return; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); dirty.current = true; if (!hasInk) setHasInk(true); };
  const up = () => { drawing.current = false; };

  const clear = () => { resetCanvas(); };

  const confirm = () => {
    if (!dirty.current) return;
    if (!signer.trim()) return;
    onConfirm({ signature_png_b64: canvasRef.current.toDataURL("image/png"), signer_name: signer.trim(), signer_role: role });
    resetCanvas();
  };

  const roleLabel = roleList.find((r) => r.value === role)?.label || role;

  return (
    <div data-testid="signature-pad">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2 text-sm">
        <label className="block"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Nome firmatario</span>
          <input value={signer} onChange={(e) => setSigner(e.target.value)} data-testid="sig-signer" className="w-full px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} /></label>
        <label className="block"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Ruolo dichiarato</span>
          <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="sig-role" className="w-full px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }}>
            {roleList.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}</select></label>
      </div>
      <canvas ref={canvasRef} width={600} height={220} data-testid="sig-canvas"
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
        className="w-full rounded-md border touch-none bg-white" style={{ borderColor: "var(--border-silver)", height: 180 }} />
      {multiSigner && <p className="text-[11px] text-amber-700 mt-1" data-testid="sig-signer-hint">Deve firmare personalmente: <strong>{roleLabel}</strong>. Passa il dispositivo al firmatario; cambiando ruolo la firma viene azzerata.</p>}
      <div className="flex gap-2 mt-2">
        <button type="button" onClick={clear} data-testid="sig-clear" className="px-3 py-2 rounded-md border text-xs flex items-center gap-1" style={{ borderColor: "var(--border-silver)" }}><Eraser className="w-3 h-3" /> Cancella</button>
        <button type="button" onClick={confirm} disabled={!hasInk || !signer.trim()} data-testid="sig-confirm" className="px-3 py-2 rounded-md text-white text-xs flex items-center gap-1 disabled:opacity-40" style={{ background: "var(--brand-dark)" }}><Check className="w-3 h-3" /> Conferma firma</button>
      </div>
    </div>
  );
}
